/**
 * Microsoft Authentication Library (MSAL) Configuration
 *
 * Configuration for Entra External ID (formerly Azure AD B2C) authentication.
 * This setup uses OAuth 2.0 and OpenID Connect protocols.
 */

import {
  Configuration,
  LogLevel,
  BrowserCacheLocation,
  PublicClientApplication,
  RedirectRequest,
  SilentRequest,
  EndSessionRequest,
} from '@azure/msal-browser';
import { logger } from '../utils/logger';

/**
 * Get environment variable with fallback
 */
const getEnvVar = (key: string, fallback: string = ''): string => {
  return import.meta.env[key] || fallback;
};

/**
 * Extract domain from authority URL for knownAuthorities
 * Authority URL format: https://creatorosb2c.ciamlogin.com/tenant-id
 * knownAuthorities needs just the domain: creatorosb2c.ciamlogin.com
 */
const getAuthorityDomain = (authority: string): string => {
  try {
    const url = new URL(authority);
    return url.hostname;
  } catch {
    // Fallback: extract domain from string
    const parts = authority.replace('https://', '').split('/');
    return parts[0] || authority;
  }
};

/**
 * Entra External ID Configuration
 *
 * IMPORTANT - Multi-Origin Redirect Support:
 * The redirectUri and postLogoutRedirectUri ALWAYS use window.location.origin
 * to ensure users are redirected back to the same origin they accessed the app from.
 *
 * This design supports:
 * - Multiple environments (dev, staging, prod)
 * - Custom domains
 * - Azure Static Web Apps default URLs
 * - Azure Static Web Apps preview/staging URLs
 *
 * All possible redirect URIs must still be registered in Entra External ID.
 * The app dynamically selects which one to use based on the current origin.
 */
export const entraConfig = {
  clientId: getEnvVar('VITE_ENTRA_CLIENT_ID'),
  authority: getEnvVar('VITE_ENTRA_AUTHORITY'),
  tenantId: getEnvVar('VITE_ENTRA_TENANT_ID'),
  apiScope: getEnvVar('VITE_ENTRA_API_SCOPE'),
  // ALWAYS use window.location.origin to ensure redirect to the same origin user accessed from
  // This allows app to work from multiple origins (dev, staging, prod, custom domains)
  redirectUri: window.location.origin + '/auth/callback',
  postLogoutRedirectUri: window.location.origin,
  knownAuthorities: [getAuthorityDomain(getEnvVar('VITE_ENTRA_AUTHORITY'))],
};

/**
 * MSAL Configuration Object
 *
 * @see https://learn.microsoft.com/en-us/entra/identity-platform/msal-js-initializing-client-applications
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: entraConfig.clientId,
    authority: entraConfig.authority,
    redirectUri: entraConfig.redirectUri,
    postLogoutRedirectUri: entraConfig.postLogoutRedirectUri,
    navigateToLoginRequestUrl: false, // Let AuthCallbackPage handle navigation to prevent conflicts
    // Known authorities for Entra External ID (domain only, not full path)
    knownAuthorities: entraConfig.knownAuthorities,
  },
  cache: {
    // Prefer sessionStorage to reduce persistence / XSS blast radius.
    // Local Vite E2E sets VITE_E2E_TEST so Playwright storageState can reuse tokens
    // (Playwright does not restore sessionStorage). Remote E2E restores sessionStorage
    // via src/e2e/fixtures.ts instead.
    cacheLocation:
      import.meta.env.VITE_E2E_TEST === 'true'
        ? BrowserCacheLocation.LocalStorage
        : BrowserCacheLocation.SessionStorage,
    storeAuthStateInCookie: false, // Set to true for IE11 or Edge legacy support
    secureCookies: window.location.protocol === 'https:',
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return; // Don't log PII

        // Suppress non-critical MSAL Info logs
        const suppressedInfoMessages = [
          'interaction_in_progress', // User interaction already in progress
          'BrowserAuthError', // Generic browser auth errors that are handled
          'acquireTokenSilent', // Token acquisition logs (too verbose)
          'CacheManager:getIdToken', // Cache reads (too verbose)
          'getAccessToken', // Token reads (too verbose)
          'handleRedirectPromise resolved', // Redirect handling (expected)
          'initialize has already been called', // Duplicate initialization (expected)
          'MSAL.js was last initialized', // Initialization info (expected)
          'handleRedirectPromise called but there is no interaction', // No-op calls (expected)
        ];

        switch (level) {
          case LogLevel.Error:
            logger.error('[MSAL Error] ' + message, undefined, { event: 'msal.error' });
            break;
          case LogLevel.Warning:
            logger.warn('[MSAL Warning] ' + message, { event: 'msal.warn' });
            break;
          case LogLevel.Info:
            if (import.meta.env.DEV) {
              // Only log Info messages that aren't in the suppressed list
              const shouldSuppress = suppressedInfoMessages.some((msg) => message.includes(msg));
              if (!shouldSuppress) {
                logger.info('[MSAL Info] ' + message, { event: 'msal.info' });
              }
            }
            break;
          case LogLevel.Verbose:
            logger.debug('[MSAL Verbose] ' + message, { event: 'msal.debug' });
            break;
        }
      },
      logLevel: import.meta.env.DEV ? LogLevel.Info : LogLevel.Warning,
      piiLoggingEnabled: false,
    },
    windowHashTimeout: 60000,
    // Reduced iframeHashTimeout: Entra External ID (CIAM) blocks silent iframe renewal
    // with X-Frame-Options: deny. Setting a shorter timeout ensures MSAL gives up quickly
    // and throws InteractionRequiredAuthError, reducing console error noise.
    iframeHashTimeout: 3000,
    // loadFrameTimeout: must be non-zero so MSAL doesn't wait forever for an iframe
    // that will always be blocked by the Entra login page's X-Frame-Options: deny header.
    loadFrameTimeout: 3000,
    // tokenRenewalOffsetSeconds: MSAL will proactively acquire a new token this many
    // seconds before the current token expires. This is the Microsoft-recommended way
    // to declare a proactive refresh buffer. 300s (5 min) matches our check interval
    // and ensures tokens are renewed before they expire in the browser.
    // @see https://learn.microsoft.com/en-us/entra/identity-platform/msal-acquire-cache-tokens
    tokenRenewalOffsetSeconds: 300,
  },
};

/**
 * Scopes for API access
 *
 * The API scope grants access to the backend API protected by Entra External ID.
 * Format: api://<api-client-id>/<scope-name>
 */
export const apiScopes = entraConfig.apiScope ? [entraConfig.apiScope] : [];

/**
 * Login Request Configuration
 *
 * Used when initiating a login flow (redirect or popup).
 *
 * IMPORTANT: Only request basic OIDC scopes during initial login.
 * API scopes should be acquired separately via acquireTokenSilent() after
 * the user is authenticated. This prevents consent/authorization issues
 * during the sign-in flow, especially for Entra External ID (CIAM).
 */
export const loginRequest: RedirectRequest = {
  scopes: ['openid', 'profile', 'email', 'offline_access'],
  // Force fresh login - always show the sign-in page with all options (email, Google, etc.)
  // This bypasses the account picker that appears when there are existing sessions
  prompt: 'login',
};

/**
 * Silent Token Request Configuration
 *
 * Used for acquiring tokens silently (without user interaction).
 */
export const tokenRequest: SilentRequest = {
  scopes: apiScopes,
  forceRefresh: false, // Use cached tokens when possible
};

/**
 * Logout Request Configuration
 */
export const logoutRequest: EndSessionRequest = {
  postLogoutRedirectUri: entraConfig.postLogoutRedirectUri,
};

/**
 * Protected Resource Map
 *
 * Maps API endpoints to required scopes for automatic token acquisition.
 */
export const protectedResources = {
  api: {
    endpoint: getEnvVar('VITE_API_URL'),
    scopes: apiScopes,
  },
};

/**
 * Initialize MSAL Instance
 *
 * Creates and returns a PublicClientApplication instance.
 * This should be created once and reused throughout the application.
 *
 * IMPORTANT: In MSAL Browser v4.x, you must call initialize() before
 * using any other MSAL methods like handleRedirectPromise() or loginRedirect().
 */
let msalInstance: PublicClientApplication | null = null;
let msalInitialized = false;
let msalInitPromise: Promise<void> | null = null;

export const getMsalInstance = (): PublicClientApplication => {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
  }
  return msalInstance;
};

/**
 * Initialize MSAL and wait for it to be ready
 * Must be called before handleRedirectPromise() or any other MSAL API
 */
export const initializeMsal = async (): Promise<PublicClientApplication> => {
  const instance = getMsalInstance();

  if (msalInitialized) {
    return instance;
  }

  if (msalInitPromise) {
    await msalInitPromise;
    return instance;
  }

  msalInitPromise = instance.initialize();
  await msalInitPromise;
  msalInitialized = true;

  return instance;
};

/**
 * Validate Entra Configuration
 *
 * Ensures all required Entra External ID configuration values are present.
 */
export const validateEntraConfig = (): void => {
  const requiredVars = [
    { key: 'VITE_ENTRA_CLIENT_ID', value: entraConfig.clientId },
    { key: 'VITE_ENTRA_AUTHORITY', value: entraConfig.authority },
    { key: 'VITE_ENTRA_API_SCOPE', value: entraConfig.apiScope },
  ];

  const missing = requiredVars.filter(({ value }) => !value);

  if (missing.length > 0) {
    const missingKeys = missing.map(({ key }) => key).join(', ');
    logger.error(`Missing required Entra External ID configuration: ${missingKeys}`, undefined, {
      event: 'msal.config.invalid',
    });
    throw new Error(`Missing required Entra External ID configuration: ${missingKeys}`);
  }

  // Validate format
  if (!entraConfig.authority.includes('.ciamlogin.com')) {
    logger.warn('Authority URL does not appear to be an Entra External ID endpoint', {
      event: 'msal.config.warn',
    });
  }
};

/**
 * Account Info Helper
 *
 * Extracts user information from MSAL account object.
 *
 * IMPORTANT: Entra External ID (CIAM) does NOT include app roles in ID tokens.
 * App roles are only present in access tokens when requesting API scopes.
 * We default to ['user'] here and rely on fetchRolesFromAccessToken thunk
 * to extract the real roles from the access token after authentication.
 *
 * @see https://learn.microsoft.com/en-us/entra/external-id/customers/concept-supported-features-customers
 */
export interface EntraUserInfo {
  userId: string; // Object ID (oid claim)
  email: string;
  name: string;
  username: string;
  roles: string[]; // Roles from JWT token
}

export const getEntraUserInfo = (account: any): EntraUserInfo | null => {
  if (!account) return null;

  // CRITICAL: Entra External ID does NOT include app roles in ID tokens
  // App roles are only present in access tokens. We default to ['user'] here
  // and rely on fetchRolesFromAccessToken to extract the real roles from the access token.
  const roles: string[] = ['user']; // Default, will be updated from access token

  return {
    userId: account.idTokenClaims?.oid || account.localAccountId,
    email:
      account.idTokenClaims?.email ||
      account.idTokenClaims?.preferred_username ||
      account.idTokenClaims?.upn ||
      (account.username?.includes('@') ? account.username : ''),
    name: account.name || account.idTokenClaims?.name || '',
    username: account.username,
    roles, // Default role, will be updated from access token
  };
};

export default {
  msalConfig,
  loginRequest,
  tokenRequest,
  logoutRequest,
  protectedResources,
  entraConfig,
  getMsalInstance,
  validateEntraConfig,
  getEntraUserInfo,
};
